/**
 * ===========================================================================
 *  STEERING — how a body actually moves once the brain has picked a goal
 * ===========================================================================
 *
 * `src/physics/bodies.js` is still a stub, so the AI integrates its own units:
 * desired velocity -> acceleration -> separation -> ground clamp. Everything
 * here is zero-allocation and is called for every live non-player unit every
 * fixed step (the *thinking* is time-sliced; the *moving* is not, or the crowd
 * would visibly stutter).
 *
 * Two things here matter more than they look:
 *
 *  - **Separation.** Bodies that interpenetrate destroy the read of a crowd
 *    instantly; bodies that are pushed apart with a hard constraint jitter.
 *    A soft, capped, distance-weighted push run at 60 Hz gives the constant
 *    shoulder-to-shoulder shuffle a Dynasty Warriors mob has.
 *  - **Arrival.** Units brake into their goal instead of stopping dead, and
 *    the brake distance is per-unit, so twenty men converging on one point
 *    arrive over ~1.5 s instead of on the same frame.
 */
import { ST, FLAG } from '../core/entities.js';
import { clamp, angleDamp, damp } from '../core/mathx.js';
import { frac01 } from './store.js';
import { WALLS, pushOut } from '../world/walls.js';

/**
 * Keep a body out of the arena's walls and take the velocity it was driving
 * INTO the wall off it, so it slides along the face instead of grinding. On
 * open ground (no walls) this is one branch.
 */
const _wo = { x: 0, z: 0, nx: 0, nz: 0 };
export function wallClamp(e, id) {
  if (!WALLS.n) return false;
  const r = e.radius[id] > 0 ? e.radius[id] : 0.45;
  if (!pushOut(e.x[id], e.z[id], r, _wo)) return false;
  e.x[id] = _wo.x; e.z[id] = _wo.z;
  const vn = e.vx[id] * _wo.nx + e.vz[id] * _wo.nz;
  if (vn < 0) { e.vx[id] -= vn * _wo.nx; e.vz[id] -= vn * _wo.nz; }
  return true;
}

const SEP_R = 1.35;         // radius of the personal-space query
const SEP_K = 7.5;          // push stiffness (m/s per metre of overlap)
const SEP_MAX = 3.4;        // never shove harder than this
const GRAVITY = -22;

/** Scratch — module scope so the hot loop never allocates. */
const _q = new Int32Array(256);

/**
 * Desired-velocity helper: head for (gx,gz), braking inside `brake` metres.
 * Writes into S.dvx/S.dvz. Returns the remaining distance.
 */
export function seek(S, id, x, z, gx, gz, speed, brake = 1.2) {
  const dx = gx - x, dz = gz - z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d < 1e-4) { S.dvx[id] = 0; S.dvz[id] = 0; return 0; }
  const v = d < brake ? speed * (d / brake) : speed;
  S.dvx[id] = (dx / d) * v; S.dvz[id] = (dz / d) * v;
  return d;
}

/** Straight-line flee from (fx,fz). */
export function flee(S, id, x, z, fx, fz, speed) {
  const dx = x - fx, dz = z - fz;
  const d = Math.sqrt(dx * dx + dz * dz) || 1;
  S.dvx[id] = (dx / d) * speed; S.dvz[id] = (dz / d) * speed;
}

/**
 * One unit of crowd separation. Only neighbours inside SEP_R are considered,
 * and the push is written straight into velocity so it reads as a shuffle in
 * the gait rather than as teleporting.
 */
export function separate(w, id, dt) {
  const e = w.entities;
  const x = e.x[id], z = e.z[id];
  const n = e.hash.query(x, z, SEP_R + 0.6, _q, _q.length);
  let px = 0, pz = 0;
  const ra = e.radius[id] || 0.42;
  for (let i = 0; i < n; i++) {
    const o = _q[i];
    if (o === id || !e.alive[o]) continue;
    const dx = x - e.x[o], dz = z - e.z[o];
    const d2 = dx * dx + dz * dz;
    const min = ra + (e.radius[o] || 0.42) + 0.14;
    if (d2 >= min * min) continue;
    // dead bodies still take up floor space, but only half as much
    const w2 = e.state[o] === ST.DEAD ? 0.45 : 1;
    let d = Math.sqrt(d2);
    if (d < 1e-3) {
      // exactly coincident (spawned on the same point): break the tie with the
      // id so the pair never oscillates
      const a = (id & 7) * 0.7854;
      px += Math.sin(a) * min; pz += Math.cos(a) * min; continue;
    }
    const push = (min - d) / d * w2;
    px += dx * push; pz += dz * push;
  }
  if (px === 0 && pz === 0) return;
  let sx = px * SEP_K, sz = pz * SEP_K;
  const s = Math.sqrt(sx * sx + sz * sz);
  if (s > SEP_MAX) { const k = SEP_MAX / s; sx *= k; sz *= k; }
  e.vx[id] += sx * dt * 12; e.vz[id] += sz * dt * 12;
}

/* ------------------------------------------------------------- tumble -- */
/**
 * A body thrown off its feet turns over in the air.
 *
 * `entities.pitch` / `.roll` existed as declared-but-never-written fields and
 * the crowd instancer built its matrix from `yaw` alone, so a man launched 2.9 m
 * held a bolt-upright standing pose for the whole arc — see the r1 physics
 * verdict and shots/unscored2/physics_lab/004_11_air_f5.png.
 *
 * The rig already lies a body down on LANDING: `knockdown` and `death_launch`
 * both take the hips to -90 deg, and `airborne` holds a -26..-31 deg lean. So
 * this is deliberately AIRBORNE-ONLY and additive to those: it turns the whole
 * root over while the body is off the ground and unwinds as it comes down, and
 * the clip owns the resting pose exactly as before. Nothing here touches a unit
 * standing on the floor, and `pitchVel`/`rollVel` are zero for all of them.
 */
const SPIN_V = 0.55;        // rad/s of pitch per m/s of lift
const SPIN_H = 0.06;        // ...and per m/s of travel
const SPIN_JIT = 0.18;      // +-18% so a sweep of twenty does not spin in lockstep
const SPIN_ROLL = 0.26;     // roll as a fraction of pitch, signed off the key
const SPIN_MIN_VY = 2.0;    // below this it is a stumble off a bank, not a launch
const LAND_T = 0.30;        // seconds before touchdown over which the turn folds away
const UNWIND_AIR = 16;      // ...and how hard, so the body meets the floor upright
const UNWIND = 9;           // how fast a landed body returns its root to upright

/**
 * Advance (or unwind) the turn-over, and SEED it on the first airborne frame.
 *
 * Seeding here rather than at the launch sites is deliberate. A blow reaches a
 * body twice in one step: the AI's `'hit'` handler puts it in ACT.LAUNCHED with
 * its own generic impulse, and then `combat.applyImpulse` overwrites vx/vy/vz
 * with the MOVESET's numbers (combat.js:632). Only the second one is the arc the
 * body actually flies. This runs on the next fixed step, so `e.vy` is already
 * the real launch speed and the spin matches the arc instead of the AI's guess —
 * and combat does not have to reach across into the AI layer to say so.
 *
 * `tumble` zeroes both angle and rate whenever the body is on the floor, so the
 * `pitch === 0 && pitchVel === 0` test fires exactly once per launch. A body
 * juggled while still in the air keeps the spin it already has.
 */
function tumble(e, id, dt, h) {
  if (h > 0.02) {
    if (e.pitchVel[id] === 0 && e.pitch[id] === 0) {
      const v0y = e.vy[id];
      if (v0y < SPIN_MIN_VY) return;              // falling off a bank, not launched
      const key = e.spawnKey[id];
      const vh = Math.sqrt(e.vx[id] * e.vx[id] + e.vz[id] * e.vz[id]);
      const j = 1 + (frac01(key, 53) * 2 - 1) * SPIN_JIT;
      const rate = (SPIN_V * v0y + SPIN_H * vh) * j;
      // negative: the body goes over BACKWARDS, head away from the blow, which
      // is the way every launch / knockdown / death clip already leans its hips
      e.pitchVel[id] = -rate;
      e.rollVel[id] = rate * SPIN_ROLL * (frac01(key, 59) < 0.5 ? -1 : 1);
    }
    e.pitch[id] += e.pitchVel[id] * dt;
    e.roll[id] += e.rollVel[id] * dt;
    // Fold the turn away over the last fraction of a second before touchdown.
    // `knockdown` takes the hips from -20 to -92 deg by itself over 0.95 s, so
    // the clip wants a body that is roughly upright when it lands, not one still
    // spinning through its own floor.
    //
    // The window is TIME to impact, not height: a fixed height window is most of
    // a 0.7 m pop and a fraction of a 2.9 m launch, so short hops lost their turn
    // and long ones kept spinning into the dirt (measured: the ch1 launcher
    // touched down at -133 deg). Damping the RATE as well as the angle matters
    // for the same reason — otherwise the fade fights the integration above and
    // the two roughly cancel.
    const vy = e.vy[id];
    if (vy < -0.5) {
      const ttl = h / -vy;
      if (ttl < LAND_T) {
        const k = Math.exp(-UNWIND_AIR * (1 - ttl / LAND_T) * dt);
        e.pitch[id] *= k; e.roll[id] *= k;
        e.pitchVel[id] *= k; e.rollVel[id] *= k;
      }
    }
    return;
  }
  if (e.pitch[id] === 0 && e.roll[id] === 0 && e.pitchVel[id] === 0 && e.rollVel[id] === 0) return;
  e.pitchVel[id] = 0; e.rollVel[id] = 0;
  const k = Math.exp(-UNWIND * dt);
  const p = e.pitch[id] * k, r = e.roll[id] * k;
  e.pitch[id] = p > -1e-3 && p < 1e-3 ? 0 : p;
  e.roll[id] = r > -1e-3 && r < 1e-3 ? 0 : r;
}

/**
 * Integrate one unit: accelerate towards the desired velocity, apply gravity
 * when airborne, clamp to the ground, turn the body, publish `speed`.
 *
 * `mode` 0 = steered (normal), 1 = ballistic (knockback / launched / dead).
 */
export function integrate(w, S, id, dt, mode) {
  const e = w.entities;
  if (mode === 0) {
    const lam = e.state[id] === ST.RUN ? 11 : 9;
    e.vx[id] = damp(e.vx[id], S.dvx[id], lam, dt);
    e.vz[id] = damp(e.vz[id], S.dvz[id], lam, dt);
  } else {
    // ballistic: drag only
    const drag = Math.exp(-4.2 * dt);
    e.vx[id] *= drag; e.vz[id] *= drag;
  }

  let y = e.y[id];
  const ground = w.terrain ? (w.terrain.walkHeightAt ? w.terrain.walkHeightAt(e.x[id], e.z[id]) : w.terrain.heightAt(e.x[id], e.z[id])) : 0;
  if (e.vy[id] !== 0 || y > ground + 0.001) {
    e.vy[id] += GRAVITY * dt;
    y += e.vy[id] * dt;
    if (y <= ground) { y = ground; e.vy[id] = 0; }
  } else y = ground;
  tumble(e, id, dt, y - ground);

  e.x[id] += e.vx[id] * dt;
  e.z[id] += e.vz[id] * dt;
  const H = w.cfg.world.half - 6;
  if (e.x[id] < -H) { e.x[id] = -H; e.vx[id] = 0; }
  else if (e.x[id] > H) { e.x[id] = H; e.vx[id] = 0; }
  if (e.z[id] < -H) { e.z[id] = -H; e.vz[id] = 0; }
  else if (e.z[id] > H) { e.z[id] = H; e.vz[id] = 0; }
  wallClamp(e, id);
  e.y[id] = y;

  const sp = Math.sqrt(e.vx[id] * e.vx[id] + e.vz[id] * e.vz[id]);
  e.speed[id] = sp;

  if (mode === 0) {
    e.yawGoal[id] = S.yawT[id];
    // a man turns his whole body faster when he is not moving his feet
    const rate = sp > 3 ? 7.5 : 10.5;
    e.yaw[id] = angleDamp(e.yaw[id], e.yawGoal[id], rate, dt);
  }
}

/** Ballistic body: launched, knocked down or dead. Returns true when landed. */
export function ballistic(w, S, id, dt) {
  const e = w.entities;
  integrate(w, S, id, dt, 1);
  const ground = w.terrain ? (w.terrain.walkHeightAt ? w.terrain.walkHeightAt(e.x[id], e.z[id]) : w.terrain.heightAt(e.x[id], e.z[id])) : 0;
  return e.y[id] <= ground + 0.002 && e.vy[id] <= 0;
}

/** Roll a body along the ground after death so it does not stop like a prop. */
export function deathSlide(w, id, dt) {
  const e = w.entities;
  const drag = Math.exp(-6.5 * dt);
  e.vx[id] *= drag; e.vz[id] *= drag;
  if (e.vy[id] !== 0) {
    e.vy[id] += GRAVITY * dt;
    e.y[id] += e.vy[id] * dt;
  }
  e.x[id] += e.vx[id] * dt; e.z[id] += e.vz[id] * dt;
  wallClamp(e, id);
  const g = w.terrain ? (w.terrain.walkHeightAt ? w.terrain.walkHeightAt(e.x[id], e.z[id]) : w.terrain.heightAt(e.x[id], e.z[id])) : 0;
  if (e.y[id] <= g) { e.y[id] = g; e.vy[id] = 0; }
  tumble(e, id, dt, e.y[id] - g);
  e.speed[id] = 0;
}

export { SEP_R, GRAVITY };
